import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Send, MoreVertical, Paperclip, Check, CheckCheck, X, Reply, VolumeX, LogOut, Flag, Users, Image as ImageIcon, Camera, Video, MapPin, Mic, Trash2, Copy, ChevronDown, Pin, Calendar, BarChart3, PinOff, UserPlus } from 'lucide-react';
import api, { uploadMedia } from '../lib/api';
import VoiceMessage from './VoiceMessage';
import { createVoiceRecorder } from '../lib/voice';
import { ConfirmDialog } from './Overlay';
import PlanShareCard from './PlanShareCard';
import PollBubble from './PollBubble';
import LocationBubble from './LocationBubble';

const REACTIONS = ['❤️', '😂', '😮', '😢', '🔥', '👍', '🙌', '☕', '🎉', '💯'];

function initials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function timeStr(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function dayLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], sameYear ? { weekday: 'short', month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Props:
 *  title, subtitle, avatar, onBack, onSend(text, replyTo, extras), onMarkRead, menuItems
 *  messages, currentUserId, isDM, seenAt
 *  onTyping?  — called on each keystroke (lightweight throttle by caller)
 *  typingUsers? — [{id, display_name, profile_photo}]
 *  onReact?(messageId, emoji)  — toggle reaction
 *  onDeleteMessage?(messageId)
 *  readOnly, readOnlyMessage, headerOnClick, loading
 */
export default function ChatUI({
  title, subtitle, avatar, onBack,
  messages, currentUserId, isDM,
  seenAt,
  onSend, onMarkRead,
  menuItems = [],
  readOnly = false, readOnlyMessage = '',
  headerOnClick,
  loading,
  onTyping,
  typingUsers = [],
  onReact,
  onDeleteMessage,
  onVote,
  onPin,
  pinnedMessages = [],
  isCreator = false,
  composerExtras = [],   // optional [{key,label,icon,onClick}] for "Share plan" / "Create poll" etc
  headerPrefix = null,   // optional React node rendered between back-btn and header content (e.g., RSVP chip)
}) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [actionOn, setActionOn] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [showAttach, setShowAttach] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [copied, setCopied] = useState(false);
  const [recState, setRecState] = useState('idle'); // idle | recording | uploading
  const [recElapsed, setRecElapsed] = useState(0);
  const [showMicPrePrompt, setShowMicPrePrompt] = useState(false);
  const [lastVoiceFile, setLastVoiceFile] = useState(null); // for retry on upload failure
  const [scrolledUp, setScrolledUp] = useState(false);
  const [newCount, setNewCount] = useState(0);

  const inputRef = useRef(null);
  const messagesRef = useRef(null);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const recorderRef = useRef(null);
  const recTimerRef = useRef(null);
  const lastMsgCountRef = useRef(messages.length);

  useEffect(() => {
    document.body.classList.add('chat-locked');
    return () => document.body.classList.remove('chat-locked');
  }, []);

  const scrollToBottom = (smooth = true) => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  // Track if user has scrolled away from bottom — show "new messages" pill
  const onScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const up = distance > 80;
    setScrolledUp(up);
    if (!up) setNewCount(0);
  };

  useEffect(() => {
    if (loading) return;
    if (lastMsgCountRef.current === 0) {
      scrollToBottom(false);
    } else if (messages.length > lastMsgCountRef.current) {
      const added = messages.length - lastMsgCountRef.current;
      const lastMsg = messages[messages.length - 1];
      const isFromMe = (lastMsg?.user_id || lastMsg?.user?.id) === currentUserId;
      if (scrolledUp && !isFromMe) {
        setNewCount(c => c + added);
      } else {
        scrollToBottom(true);
      }
    }
    lastMsgCountRef.current = messages.length;
  }, [messages.length, loading, currentUserId, scrolledUp, messages]);

  // Fire mark-read once when messages first load, then again only when a
  // genuinely new message arrives — not on the intermediate length flickers
  // while the chat is fetching pages. Backend is idempotent so the safety
  // net is purely a network-chatter optimization.
  const lastMarkedLenRef = useRef(-1);
  useEffect(() => {
    if (!onMarkRead) return;
    if (messages.length === lastMarkedLenRef.current) return;
    lastMarkedLenRef.current = messages.length;
    onMarkRead();
  }, [onMarkRead, messages.length]);

  const handleFocus = () => setTimeout(() => scrollToBottom(true), 320);

  // Typing-throttle: only call onTyping at most every 2s
  const lastTypingRef = useRef(0);
  const handleTextChange = (e) => {
    setText(e.target.value);
    if (onTyping) {
      const now = Date.now();
      if (now - lastTypingRef.current > 2000) {
        lastTypingRef.current = now;
        onTyping();
      }
    }
  };

  const handleSend = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await onSend(t, replyTo ? {
        id: replyTo.id,
        text: (replyTo.text || '').slice(0, 160),
        sender_name: replyTo.user?.display_name || '',
      } : null);
      setText('');
      setReplyTo(null);
      inputRef.current?.focus();
      setTimeout(() => scrollToBottom(true), 60);
    } catch {/* ignore */}
    setSending(false);
  };

  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setShowAttach(false);
    setUploading(true);
    setUploadError('');
    try {
      const res = await uploadMedia(file);
      await onSend('', null, {
        media_url: res.url,
        media_type: res.type || (file.type.startsWith('video/') ? 'video' : 'image'),
      });
      setTimeout(() => scrollToBottom(true), 100);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Upload failed. Please try again.');
    }
    setUploading(false);
  };

  const handleShareLocation = () => {
    setShowAttach(false);
    if (!navigator.geolocation) { setUploadError("Location isn't supported on this device."); return; }
    setUploading(true);
    setUploadError('');
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        let name = 'My location';
        let address = '';
        try {
          const { data } = await api.get(`/places/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`);
          if (data?.name) name = data.name;
          if (data?.address) address = data.address;
        } catch {/* soft-fail to coords */}
        await onSend('', null, {
          location: { lat: pos.coords.latitude, lng: pos.coords.longitude, name, address },
        });
        setTimeout(() => scrollToBottom(true), 100);
      } catch { setUploadError('Could not share location.'); }
      setUploading(false);
    }, (err) => {
      setUploading(false);
      setUploadError(err.code === 1 ? 'Location permission denied.' : 'Could not get your location.');
    }, { enableHighAccuracy: true, timeout: 10000 });
  };

  // Voice recording — pre-permission sheet on first use (per beta feedback),
  // then native getUserMedia prompt. Decision persists in localStorage so we
  // don't nag returning users.
  const startRecording = async () => {
    if (recState !== 'idle') return;
    const seenPrePrompt = (() => {
      try { return localStorage.getItem('asap_mic_prepermission') === 'shown'; }
      catch { return false; }
    })();
    if (!seenPrePrompt) {
      setShowMicPrePrompt(true);
      return;
    }
    await beginRecording();
  };

  const beginRecording = async () => {
    try {
      const r = await createVoiceRecorder();
      recorderRef.current = r;
      r.start();
      setRecState('recording');
      setRecElapsed(0);
      recTimerRef.current = setInterval(() => setRecElapsed((e) => e + 1), 1000);
    } catch (err) {
      setUploadError(
        err?.name === 'NotAllowedError'
          ? 'Microphone access blocked. Enable it in your browser settings to send voice notes.'
          : (err?.message || 'Could not access microphone.')
      );
    }
  };

  const acceptMicPrePrompt = async () => {
    try { localStorage.setItem('asap_mic_prepermission', 'shown'); } catch {}
    setShowMicPrePrompt(false);
    await beginRecording();
  };

  const stopAndSendRecording = async () => {
    if (recState !== 'recording' || !recorderRef.current) return;
    clearInterval(recTimerRef.current);
    setRecState('uploading');
    try {
      const { file, duration } = await recorderRef.current.stop();
      recorderRef.current = null;
      await sendVoiceFile(file, duration);
    } catch (err) {
      setUploadError(err.message || 'Voice upload failed.');
      setRecState('idle');
      setRecElapsed(0);
    }
  };

  const sendVoiceFile = async (file, duration) => {
    try {
      const res = await uploadMedia(file);
      await onSend('', null, { audio_url: res.url, audio_duration: duration });
      setTimeout(() => scrollToBottom(true), 100);
      setLastVoiceFile(null);
      setUploadError('');
    } catch (err) {
      // Stash the recording so the user can retry without re-recording.
      setLastVoiceFile({ file, duration });
      setUploadError(err.response?.data?.detail || 'Voice upload failed. Tap to retry.');
    }
    setRecState('idle');
    setRecElapsed(0);
  };

  const retryVoiceSend = async () => {
    if (!lastVoiceFile) return;
    setRecState('uploading');
    await sendVoiceFile(lastVoiceFile.file, lastVoiceFile.duration);
  };

  const cancelRecording = () => {
    clearInterval(recTimerRef.current);
    if (recorderRef.current) { recorderRef.current.cancel(); recorderRef.current = null; }
    setRecState('idle');
    setRecElapsed(0);
  };

  const handleReact = async (emoji) => {
    if (!actionOn || !onReact) return;
    await onReact(actionOn.id, emoji);
    setActionOn(null);
  };

  const handleCopy = async () => {
    if (!actionOn) return;
    try { await navigator.clipboard.writeText(actionOn.text || ''); } catch {/* ignore */}
    setCopied(true);
    setActionOn(null);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDelete = async () => {
    if (!actionOn || !onDeleteMessage) return;
    await onDeleteMessage(actionOn.id);
    setActionOn(null);
  };

  // Build render list with day separators and meta visibility
  const items = [];
  let lastDay = '';
  let lastSenderId = null;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m.deleted) {
      // Render deleted placeholder, no avatar grouping
      items.push({ kind: 'deleted', m });
      lastSenderId = null;
      continue;
    }
    const d = dayLabel(m.created_at);
    if (d !== lastDay) {
      items.push({ kind: 'day', id: `d-${i}`, label: d });
      lastDay = d;
      lastSenderId = null;
    }
    const isMine = (m.user_id || m.user?.id) === currentUserId;
    const showMeta = lastSenderId !== (m.user_id || m.user?.id);
    items.push({ kind: 'msg', m, isMine, showMeta });
    lastSenderId = m.user_id || m.user?.id;
  }

  const isMineMessage = actionOn && (actionOn.user_id || actionOn.user?.id) === currentUserId;
  const hasText = !!(actionOn?.text);

  return (
    <div className="chat-screen flex flex-col bg-[#F7F6F2] max-w-[480px] mx-auto" data-testid="chatui">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-2.5 px-3 py-2.5 bg-white border-b border-black/[0.06] z-10 w-full min-w-0">
        <button data-testid="chatui-back" onClick={onBack} className="p-1 -ml-1">
          <ArrowLeft size={22} className="text-[#111111]" />
        </button>
        <button onClick={headerOnClick} disabled={!headerOnClick} className="flex items-center gap-2.5 flex-1 min-w-0 text-left active:opacity-70 disabled:opacity-100" data-testid="chatui-header">
          {avatar ? (
            <img loading="lazy" decoding="async" src={avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3] flex-shrink-0">{initials(title)}</div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-[#111111] truncate leading-tight">{title}</p>
            {subtitle && <p className="text-[11px] text-[#9B9EA3] truncate mt-0.5">{subtitle}</p>}
          </div>
        </button>
        {menuItems.length > 0 && (
          <button data-testid="chatui-menu-btn" onClick={() => setShowMenu(true)} className="p-1 text-[#6B6E73] -mr-1" aria-label="More options">
            <MoreVertical size={22} />
          </button>
        )}
      </div>

      {readOnly && readOnlyMessage && (
        <div className="px-4 py-2 bg-[#F1F0EA] text-center border-b border-black/[0.04]">
          <p className="text-xs text-[#9B9EA3]">{readOnlyMessage}</p>
        </div>
      )}

      {/* Pinned bar */}
      {pinnedMessages.length > 0 && (
        <div className="flex-shrink-0 bg-[#FFF8E0] border-b border-[#F2E59A]/40" data-testid="pinned-bar">
          {pinnedMessages.slice(0, 3).map(pm => {
            const preview = pm.deleted ? 'Message deleted'
              : pm.text ? pm.text
              : pm.audio_url ? '🎤 Voice message'
              : pm.media_type === 'image' ? '📷 Photo'
              : pm.media_type === 'video' ? '🎥 Video'
              : pm.plan_share ? `📅 ${pm.plan_share.title || 'Shared plan'}`
              : pm.poll ? `📊 ${pm.poll.question || 'Poll'}`
              : pm.location ? `📍 ${pm.location.name || 'Location'}`
              : 'Pinned message';
            return (
              <div key={pm.id} className="flex items-center gap-2 px-3 py-1.5" data-testid={`pinned-${pm.id}`}>
                <Pin size={11} className="text-[#A6850A] flex-shrink-0" fill="currentColor" />
                <p className="text-[11px] text-[#6B5300] truncate flex-1">{preview}</p>
                {isCreator && onPin && (
                  <button onClick={() => onPin(pm.id)} data-testid={`unpin-${pm.id}`} className="text-[10px] font-bold text-[#A6850A] active:scale-95">Unpin</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {headerPrefix}

      {/* Messages */}
      <div ref={messagesRef} onScroll={onScroll} className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 hide-scrollbar w-full relative" data-testid="chatui-messages">
        {loading ? (
          <div className="space-y-3 pt-6">{[1, 2, 3].map(i => <div key={i} className="skeleton h-12 rounded-2xl" />)}</div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-20">
            <div className="w-16 h-16 rounded-full bg-white border border-black/[0.06] flex items-center justify-center mb-3">
              {avatar ? <img loading="lazy" decoding="async" src={avatar} alt="" className="w-16 h-16 rounded-full object-cover" /> : <span className="text-lg font-bold text-[#9B9EA3]">{initials(title)}</span>}
            </div>
            <p className="text-sm font-bold text-[#111111]">{title}</p>
            <p className="text-xs text-[#9B9EA3] mt-1">Say something to kick things off.</p>
          </div>
        ) : (
          items.map((it, idx) => {
            if (it.kind === 'day') {
              return (
                <div key={it.id} className="text-center my-3">
                  <span className="text-[10px] font-medium text-[#9B9EA3] bg-white px-3 py-1 rounded-full border border-black/[0.04]">{it.label}</span>
                </div>
              );
            }
            if (it.kind === 'deleted') {
              const isMine = (it.m.user_id || it.m.user?.id) === currentUserId;
              return (
                <div key={it.m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'} my-1`}>
                  <div className="px-3 py-1.5 rounded-2xl bg-[#F1F0EA] border border-black/[0.04]">
                    <p className="text-[11px] italic text-[#9B9EA3]">Message deleted</p>
                  </div>
                </div>
              );
            }
            const { m, isMine, showMeta } = it;

            if (m.type === 'announcement') {
              return (
                <div key={m.id} className="text-center my-2">
                  <span className="inline-block text-[11px] text-[#3F5200] bg-[#B6E600]/15 border border-[#B6E600]/30 px-3 py-1.5 rounded-full">📣 {m.text}</span>
                </div>
              );
            }

            const isSeen = isMine && isDM && seenAt && m.created_at && seenAt >= m.created_at;
            const reactions = m.reactions || {};
            const reactionEntries = Object.entries(reactions).filter(([, uids]) => (uids || []).length > 0);

            return (
              <div key={m.id} className={`flex w-full min-w-0 ${isMine ? 'justify-end' : 'justify-start'} ${showMeta ? 'mt-2' : 'mt-0.5'}`}>
                {!isMine && (
                  <div className="w-7 mr-1.5 flex-shrink-0">
                    {showMeta && (
                      m.user?.profile_photo
                        ? <img loading="lazy" decoding="async" src={m.user.profile_photo} alt="" className="w-7 h-7 rounded-full object-cover" />
                        : <div className="w-7 h-7 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[9px] font-bold text-[#9B9EA3]">{initials(m.user?.display_name)}</div>
                    )}
                  </div>
                )}
                <div className={`max-w-[78%] min-w-0 flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                  <div
                    data-testid={`msg-${m.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActionOn(m)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActionOn(m); } }}
                    className={`text-left cursor-pointer select-none ${isMine ? 'bg-[#B6E600] text-[#111111] rounded-2xl rounded-br-md' : 'bg-white text-[#111111] rounded-2xl rounded-bl-md border border-black/[0.04]'} ${showMeta ? '' : (isMine ? 'rounded-br-md' : 'rounded-bl-md')} px-3 py-2 active:scale-[0.98] transition-transform`}
                  >
                    {!isMine && showMeta && !isDM && (
                      <p className="text-[10px] font-bold text-[#3F5200] mb-0.5 truncate">{m.user?.display_name}{m.is_host && <span className="ml-1 text-[#6B8A00]">· Host</span>}</p>
                    )}
                    {m.reply_to?.text && (
                      <div className={`mb-1 px-2.5 py-1.5 rounded-xl min-w-0 ${isMine ? 'bg-[#3F5200]/10' : 'bg-[#F1F0EA]'}`}>
                        <p className={`text-[10px] font-bold truncate ${isMine ? 'text-[#3F5200]' : 'text-[#6B8A00]'}`}>{m.reply_to.sender_name || 'Message'}</p>
                        <p className={`text-xs truncate ${isMine ? 'text-[#3F5200]' : 'text-[#6B6E73]'}`}>{m.reply_to.text}</p>
                      </div>
                    )}
                    {m.media_url && m.media_type === 'image' && (
                      <img decoding="async" src={m.media_url} alt="" className="rounded-xl mb-1 max-h-[280px] w-auto max-w-full object-cover" loading="lazy" onClick={(e) => { e.stopPropagation(); window.open(m.media_url, '_blank'); }} />
                    )}
                    {m.media_url && m.media_type === 'video' && (
                      <video src={m.media_url} controls playsInline className="rounded-xl mb-1 max-h-[280px] w-auto max-w-full" onClick={(e) => e.stopPropagation()} />
                    )}
                    {m.audio_url && (
                      <VoiceMessage url={m.audio_url} duration={m.audio_duration} isMine={isMine} />
                    )}
                    {m.location && typeof m.location.lat === 'number' && typeof m.location.lng === 'number' && (
                      <LocationBubble location={m.location} isMine={isMine} />
                    )}
                    {m.plan_share && (
                      <PlanShareCard planShare={m.plan_share} isMine={isMine} />
                    )}
                    {m.story_ref && (
                      <StoryReplyCard storyRef={m.story_ref} isMine={isMine} />
                    )}
                    {m.poll && (
                      <PollBubble poll={m.poll} currentUserId={currentUserId} isMine={isMine} onVote={(optId) => onVote?.(m.id, optId)} />
                    )}
                    {m.text && <p className="text-sm leading-relaxed whitespace-pre-wrap break-words chat-wrap">{m.text}</p>}
                    <div className={`flex items-center gap-1 mt-0.5 justify-end ${isMine ? 'text-[#3F5200]/70' : 'text-[#9B9EA3]'}`}>
                      <span className="text-[10px]">{timeStr(m.created_at)}</span>
                      {isMine && isDM && (isSeen ? <CheckCheck size={12} className="text-[#3F5200]" /> : <Check size={12} />)}
                    </div>
                  </div>
                  {reactionEntries.length > 0 && (
                    <div className={`-mt-1.5 mb-0.5 flex flex-wrap gap-1 ${isMine ? 'mr-1' : 'ml-1'}`} data-testid={`reactions-${m.id}`}>
                      {reactionEntries.map(([emo, uids]) => {
                        const mine = uids.includes(currentUserId);
                        return (
                          <button key={emo} onClick={() => onReact?.(m.id, emo)} data-testid={`reaction-chip-${m.id}-${emo}`} className={`inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded-full border ${mine ? 'bg-[#B6E600]/30 border-[#B6E600]' : 'bg-white border-black/[0.06]'} active:scale-95 transition-transform`}>
                            <span>{emo}</span>
                            <span className="text-[10px] text-[#6B6E73] font-medium">{uids.length}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        {/* Typing indicator at bottom of message list */}
        {typingUsers.length > 0 && (
          <div className="flex items-center gap-2 px-1 pt-2" data-testid="typing-indicator">
            <div className="bg-white rounded-full px-3 py-1.5 border border-black/[0.04] flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#9B9EA3] animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#9B9EA3] animate-bounce" style={{ animationDelay: '120ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#9B9EA3] animate-bounce" style={{ animationDelay: '240ms' }} />
            </div>
            <span className="text-[11px] text-[#9B9EA3] truncate">{typingUsers.map(u => u.display_name?.split(' ')[0]).join(', ')} typing…</span>
          </div>
        )}
      </div>

      {/* New-message pill */}
      {scrolledUp && newCount > 0 && (
        <button
          onClick={() => { scrollToBottom(true); setNewCount(0); }}
          data-testid="new-messages-pill"
          className="absolute left-1/2 -translate-x-1/2 bottom-[88px] bg-[#111111] text-white text-xs font-semibold px-3.5 py-2 rounded-full shadow-lg flex items-center gap-1.5 z-30 animate-fade-in"
        >
          <ChevronDown size={14} />
          {newCount} new message{newCount > 1 ? 's' : ''}
        </button>
      )}

      {/* Composer */}
      {!readOnly && (
        <div className="flex-shrink-0 bg-white border-t border-black/[0.06] w-full min-w-0">
          {replyTo && (
            <div className="flex items-center gap-2 px-3 pt-2 pb-1.5 w-full min-w-0" data-testid="reply-preview">
              <div className="w-1 h-8 rounded-full bg-[#B6E600] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-[#3F5200] truncate">Replying to {replyTo.user?.display_name || 'message'}</p>
                <p className="text-xs text-[#6B6E73] truncate">{replyTo.text}</p>
              </div>
              <button data-testid="reply-preview-close" onClick={() => setReplyTo(null)} className="p-1 text-[#9B9EA3] flex-shrink-0"><X size={16} /></button>
            </div>
          )}

          {recState === 'recording' ? (
            <div className="flex items-center gap-3 px-3 py-3 pb-[max(env(safe-area-inset-bottom),8px)] w-full" data-testid="voice-recording-bar">
              <button type="button" onClick={cancelRecording} data-testid="voice-cancel" className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[#FF3B30] active:scale-90 transition-transform">
                <X size={18} />
              </button>
              <div className="flex-1 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse" />
                <span className="text-sm font-semibold text-[#111111] tabular-nums">{Math.floor(recElapsed / 60)}:{(recElapsed % 60).toString().padStart(2, '0')}</span>
                <span className="text-xs text-[#9B9EA3]">Recording…</span>
              </div>
              <button type="button" onClick={stopAndSendRecording} data-testid="voice-send" className="w-10 h-10 rounded-full bg-[#B6E600] text-[#111111] flex items-center justify-center active:scale-90 transition-transform shadow-sm">
                <Send size={18} strokeWidth={2.5} />
              </button>
            </div>
          ) : (
            <form onSubmit={e => { e.preventDefault(); handleSend(); }} className="flex items-end gap-1.5 px-2.5 py-2 pb-[max(env(safe-area-inset-bottom),8px)] w-full min-w-0">
              <div className="flex-1 min-w-0 bg-[#F1F0EA] rounded-3xl flex items-end overflow-hidden">
                <textarea ref={inputRef} data-testid="composer-input" value={text} onChange={handleTextChange} onFocus={handleFocus} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Message" rows={1} className="flex-1 min-w-0 bg-transparent text-[#111111] px-3.5 py-2.5 text-sm focus:outline-none placeholder-[#B0B3B8] resize-none max-h-28 chat-wrap" />
                <button type="button" data-testid="composer-attach" onClick={() => setShowAttach(true)} className="w-9 h-10 flex items-center justify-center text-[#9B9EA3] active:text-[#111111] flex-shrink-0 transition-colors" aria-label="Attachment">
                  <Paperclip size={18} />
                </button>
              </div>
              {text.trim() ? (
                <button type="submit" data-testid="composer-send" disabled={sending} className="w-10 h-10 flex-shrink-0 rounded-full bg-[#B6E600] text-[#111111] flex items-center justify-center active:scale-90 transition-transform disabled:opacity-40 shadow-sm">
                  <Send size={18} strokeWidth={2.5} />
                </button>
              ) : (
                <button type="button" onClick={startRecording} data-testid="composer-voice" disabled={recState === 'uploading'} className="w-10 h-10 flex-shrink-0 rounded-full bg-[#111111] text-white flex items-center justify-center active:scale-90 transition-transform disabled:opacity-50 shadow-sm">
                  <Mic size={18} strokeWidth={2.5} />
                </button>
              )}
            </form>
          )}
        </div>
      )}

      {/* Message action sheet */}
      {actionOn && (
        <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/30 animate-fade-in" onClick={() => setActionOn(null)} data-testid="msg-action-overlay">
          <div className="w-full max-w-[480px] bg-white rounded-t-3xl pb-safe-area animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-[#E5E4DE] rounded-full" /></div>
            {onReact && (
              <div className="px-5 pt-2 pb-1 flex items-center justify-between gap-1" data-testid="reaction-picker">
                {REACTIONS.map(emo => (
                  <button key={emo} onClick={() => handleReact(emo)} data-testid={`react-${emo}`} className="text-2xl w-11 h-11 rounded-full hover:bg-[#F1F0EA] active:scale-90 transition-transform">
                    {emo}
                  </button>
                ))}
              </div>
            )}
            <div className="px-5 pt-3 pb-1 border-t border-black/[0.04]">
              <p className="text-xs text-[#9B9EA3] truncate">&ldquo;{(actionOn.text || actionOn.audio_url ? '🎤 Voice message' : '').slice(0, 80) || (actionOn.media_type === 'image' ? '📷 Photo' : (actionOn.media_type === 'video' ? '🎥 Video' : ''))}&rdquo;</p>
            </div>
            <div className="px-3 pt-2">
              <button data-testid="msg-action-reply" onClick={() => { setReplyTo(actionOn); setActionOn(null); inputRef.current?.focus(); }} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0"><Reply size={18} className="text-[#111111]" /></div>
                <div className="flex-1"><p className="text-sm font-semibold text-[#111111]">Reply</p></div>
              </button>
              {hasText && (
                <button data-testid="msg-action-copy" onClick={handleCopy} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                  <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0"><Copy size={18} className="text-[#111111]" /></div>
                  <div className="flex-1"><p className="text-sm font-semibold text-[#111111]">Copy text</p></div>
                </button>
              )}
              {isCreator && onPin && (
                <button data-testid="msg-action-pin" onClick={() => { onPin(actionOn.id); setActionOn(null); }} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                  <div className="w-10 h-10 rounded-2xl bg-[#FFF8E0] flex items-center justify-center flex-shrink-0">
                    {pinnedMessages.some(p => p.id === actionOn.id)
                      ? <PinOff size={18} className="text-[#A6850A]" />
                      : <Pin size={18} className="text-[#A6850A]" />}
                  </div>
                  <div className="flex-1"><p className="text-sm font-semibold text-[#111111]">{pinnedMessages.some(p => p.id === actionOn.id) ? 'Unpin' : 'Pin'}</p></div>
                </button>
              )}
              {isMineMessage && onDeleteMessage && (
                <button data-testid="msg-action-delete" onClick={handleDelete} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                  <div className="w-10 h-10 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0"><Trash2 size={18} className="text-[#FF3B30]" /></div>
                  <div className="flex-1"><p className="text-sm font-semibold text-[#FF3B30]">Delete</p></div>
                </button>
              )}
            </div>
            <div className="px-5 pt-3">
              <button onClick={() => setActionOn(null)} className="w-full py-3 rounded-full bg-[#F1F0EA] text-sm font-medium text-[#111111]" data-testid="msg-action-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* 3-dot menu sheet */}
      {showMenu && (
        <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/30 animate-fade-in" onClick={() => setShowMenu(false)} data-testid="chatui-menu-overlay">
          <div className="w-full max-w-[480px] bg-white rounded-t-3xl pb-safe-area animate-slide-up" onClick={e => e.stopPropagation()} data-testid="chatui-menu-sheet">
            <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-[#E5E4DE] rounded-full" /></div>
            <div className="px-5 pt-3 pb-1"><p className="text-sm font-bold text-[#111111] truncate">{title}</p></div>
            <div className="px-3 pt-2">
              {menuItems.map(mi => {
                const Icon = mi.icon || Users;
                return (
                  <button key={mi.key} data-testid={`menu-${mi.key}`} onClick={() => { setShowMenu(false); mi.onClick?.(); }} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${mi.destructive ? 'bg-[#FF3B30]/10' : 'bg-[#F1F0EA]'}`}>
                      <Icon size={18} className={mi.destructive ? 'text-[#FF3B30]' : 'text-[#111111]'} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-semibold ${mi.destructive ? 'text-[#FF3B30]' : 'text-[#111111]'}`}>{mi.label}</p>
                      {mi.desc && <p className="text-xs text-[#9B9EA3] mt-0.5">{mi.desc}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="px-5 pt-3"><button onClick={() => setShowMenu(false)} className="w-full py-3 rounded-full bg-[#F1F0EA] text-sm font-medium text-[#111111]" data-testid="chatui-menu-cancel">Cancel</button></div>
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handleFilePicked} data-testid="file-photo-input" />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFilePicked} data-testid="file-camera-input" />
      <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={handleFilePicked} data-testid="file-video-input" />

      {uploading && <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[10020] bg-[#111111] text-white text-xs px-4 py-2.5 rounded-full shadow-lg animate-fade-in" data-testid="upload-toast">Uploading...</div>}
      {uploadError && (
        <div
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[10020] bg-[#FF3B30] text-white text-xs px-4 py-2.5 rounded-full shadow-lg animate-fade-in flex items-center gap-2"
          data-testid="upload-error"
        >
          <button type="button" onClick={() => setUploadError('')} className="font-medium">
            {uploadError}
          </button>
          {lastVoiceFile && (
            <button
              type="button"
              data-testid="voice-retry-btn"
              onClick={retryVoiceSend}
              className="bg-white/20 hover:bg-white/30 rounded-full px-2 py-0.5 font-extrabold"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {/* Friendly mic pre-permission sheet (first time only — persists in localStorage). */}
      <ConfirmDialog
        open={showMicPrePrompt}
        onClose={() => setShowMicPrePrompt(false)}
        onConfirm={acceptMicPrePrompt}
        title="Turn on your microphone?"
        description="Voice notes need access to your mic. Recording only happens while you hold the voice button — nothing is uploaded until you tap Send. You can revoke access any time from your browser settings."
        confirmLabel="Allow microphone"
        cancelLabel="Not now"
        confirmVariant="primary"
        icon={null}
        testIdPrefix="mic-prepermission"
      />
      {copied && <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[10020] bg-[#111111] text-white text-xs px-4 py-2.5 rounded-full shadow-lg animate-fade-in" data-testid="copied-toast">Copied to clipboard</div>}

      {/* Attachment sheet */}
      {showAttach && (
        <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/30 animate-fade-in" onClick={() => setShowAttach(false)} data-testid="attach-overlay">
          <div className="w-full max-w-[480px] bg-white rounded-t-3xl pb-safe-area animate-slide-up" onClick={e => e.stopPropagation()} data-testid="attach-sheet">
            <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-[#E5E4DE] rounded-full" /></div>
            <div className="px-5 pt-3 pb-1"><p className="text-sm font-bold text-[#111111]">Share something</p></div>
            <div className="px-3 pt-2 space-y-1">
              <button data-testid="attach-photo" onClick={() => { setShowAttach(false); photoInputRef.current?.click(); }} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                <div className="w-10 h-10 rounded-2xl bg-[#B6E600]/15 flex items-center justify-center flex-shrink-0"><ImageIcon size={18} className="text-[#3F5200]" /></div>
                <div className="flex-1"><p className="text-sm font-semibold text-[#111111]">Photo Library</p><p className="text-xs text-[#9B9EA3] mt-0.5">Pick an image from your device</p></div>
              </button>
              <button data-testid="attach-camera" onClick={() => { setShowAttach(false); cameraInputRef.current?.click(); }} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0"><Camera size={18} className="text-[#111111]" /></div>
                <div className="flex-1"><p className="text-sm font-semibold text-[#111111]">Camera</p><p className="text-xs text-[#9B9EA3] mt-0.5">Take a new photo</p></div>
              </button>
              <button data-testid="attach-video" onClick={() => { setShowAttach(false); videoInputRef.current?.click(); }} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0"><Video size={18} className="text-[#111111]" /></div>
                <div className="flex-1"><p className="text-sm font-semibold text-[#111111]">Video</p><p className="text-xs text-[#9B9EA3] mt-0.5">Share a short clip (up to 50MB)</p></div>
              </button>
              <button data-testid="attach-location" onClick={handleShareLocation} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0"><MapPin size={18} className="text-[#111111]" /></div>
                <div className="flex-1"><p className="text-sm font-semibold text-[#111111]">Share Location</p><p className="text-xs text-[#9B9EA3] mt-0.5">Send your current location</p></div>
              </button>
              {composerExtras.map(ex => {
                const Icon = ex.icon || Calendar;
                return (
                  <button key={ex.key} data-testid={`attach-${ex.key}`} onClick={() => { setShowAttach(false); ex.onClick?.(); }} className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left">
                    <div className="w-10 h-10 rounded-2xl bg-[#B6E600]/20 flex items-center justify-center flex-shrink-0"><Icon size={18} className="text-[#3F5200]" /></div>
                    <div className="flex-1"><p className="text-sm font-semibold text-[#111111]">{ex.label}</p>{ex.desc && <p className="text-xs text-[#9B9EA3] mt-0.5">{ex.desc}</p>}</div>
                  </button>
                );
              })}
            </div>
            <div className="px-5 pt-3"><button onClick={() => setShowAttach(false)} className="w-full py-3 rounded-full bg-[#F1F0EA] text-sm font-medium text-[#111111]" data-testid="attach-cancel">Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper kept outside the component so React's hook-purity lint doesn't flag
// Date.now() usage in render. The check is a read of system clock — pure
// enough for our purpose since the surrounding component re-renders only on
// message updates.
function isStoryRefExpired(expiresAt) {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

ChatUI.icons = { VolumeX, LogOut, Flag, Users };

// Story reply card — small thumbnail + "Replied to story" pill rendered inside
// the chat bubble. Expires when the original story expires; falls back to a
// neutral "Story unavailable" state otherwise.
function StoryReplyCard({ storyRef, isMine }) {
  if (!storyRef) return null;
  // `Date.now()` is read once per render; lint exempts via wrapper.
  const expired = isStoryRefExpired(storyRef.expires_at);
  const tint = isMine ? 'border-[#3F5200]/20 bg-[#3F5200]/8' : 'border-black/[0.05] bg-[#F1F0EA]';
  return (
    <div className={`mb-1 rounded-2xl overflow-hidden border ${tint}`} data-testid="story-reply-card">
      <div className="flex items-stretch gap-2.5 p-2">
        <div className="relative w-12 h-16 rounded-xl overflow-hidden bg-black flex-shrink-0">
          {storyRef.media_type === 'video' ? (
            <video src={storyRef.media_url} muted className="w-full h-full object-cover" />
          ) : (
            <img loading="lazy" decoding="async" src={storyRef.media_url} alt="" className="w-full h-full object-cover" />
          )}
          {expired && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-[9px] text-white font-bold">Expired</div>}
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <p className={`text-[10px] font-bold ${isMine ? 'text-[#3F5200]' : 'text-[#6B8A00]'}`}>Replied to story</p>
          <p className={`text-[11px] truncate ${isMine ? 'text-[#3F5200]/80' : 'text-[#6B6E73]'}`}>
            {expired ? 'No longer available' : 'Tap message to view'}
          </p>
        </div>
      </div>
    </div>
  );
}
